import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import classNames from "../../utils/classNames";
import {
  dayLabel,
  dispatchedTrend,
  formatDuration,
  ILeadTime,
  IOrdersOverview,
  shares,
  snapshotLabel,
  timeTrend,
  Trend,
} from "../../utils/ordersOverview";
import Card from "../shared/Card/Card";
import Tooltip from "../shared/Tooltip";
import EbayOrdersStats from "./EbayOrdersStats";

const REFRESH_MS = 60_000;

// Remembered per browser, so someone who keeps it collapsed doesn't have to collapse it every visit
const OPEN_KEY = "ordersOverview.open";
const readOpen = () => {
  try { return localStorage.getItem(OPEN_KEY) !== "0"; } catch { return true; }
};

// Status colours: each always sits beside its label and count, never colour alone
const LIGHTS = [
  { key: "overdue", label: "Overdue", sub: "Past their dispatch date", bar: "bg-red-500", tint: "bg-red-50 border-red-200", ink: "text-red-700", Icon: ExclamationTriangleIcon },
  { key: "dueToday", label: "Must go today", sub: "Due out before midnight", bar: "bg-amber-400", tint: "bg-amber-50 border-amber-200", ink: "text-amber-700", Icon: ClockIcon },
  { key: "later", label: "Can wait", sub: "Due tomorrow or later", bar: "bg-emerald-500", tint: "bg-emerald-50 border-emerald-200", ink: "text-emerald-700", Icon: CheckCircleIcon },
  { key: "noDate", label: "No date", sub: "Collections, or no shipping policy", bar: "bg-gray-300", tint: "bg-gray-50 border-gray-200", ink: "text-gray-600", Icon: MinusCircleIcon },
] as const;

const trendInk: Record<Trend, string> = {
  better: "text-emerald-700",
  worse: "text-red-600",
  same: "text-gray-500",
  none: "text-gray-400",
};

/**
 * The warehouse at a glance, at the top of the Orders page: what's still to go and how urgent it is,
 * what's gone today, and how long each step is taking. Whole warehouse only - nothing per person.
 * Falls back to the old stats card if the API doesn't have the overview yet.
 */
export default function OrdersOverview() {
  const { get } = useApiHelper(apiConfig("OrdersOverview"));
  const [data, setData] = useState<IOrdersOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(readOpen);

  useEffect(() => {
    let cancelled = false;

    const load = () =>
      get<{ data?: IOrdersOverview }>("/part/orders/overview")
        .then(res => {
          if (cancelled) return;
          if (res?.data) setData(res.data);
          else if (!data) setFailed(true);
        })
        .catch(() => !cancelled && !data && setFailed(true));

    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (isOpen: boolean) => {
    setOpen(isOpen);
    try { localStorage.setItem(OPEN_KEY, isOpen ? "1" : "0"); } catch { /* private window: just don't remember */ }
  };

  if (failed && !data) return <EbayOrdersStats />;

  const updated = data ? DateTime.fromISO(data.generatedAt, { zone: "utc" }).setZone("Europe/London").toFormat("HH:mm") : null;

  return (
    <Card
      title="Warehouse today"
      collapsible
      defaultOpen={open}
      onToggle={toggle}
      bodyClassName="p-4 sm:p-6"
      headerRight={
        !open && data ? <Summary data={data} />
          : updated && <span className="text-xs font-normal text-gray-400 mr-2">Updated {updated} · refreshes every minute</span>
      }
    >
      {!data ? (
        <div className="h-48 animate-pulse rounded-lg bg-gray-100" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <ToGo data={data} />
            <Dispatched data={data} />
          </div>
          <Speed data={data} />
        </div>
      )}
    </Card>
  );
}

/** The collapsed card: orders to go and the traffic light counts, in the title bar. */
function Summary({ data }: { data: IOrdersOverview }) {
  const lights = LIGHTS.filter(l => l.key !== "noDate");

  return (
    <span className="flex flex-wrap items-center justify-end gap-2 text-sm font-normal">
      <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">
        <span className="font-bold text-gray-900">{data.openOrders.toLocaleString()}</span> to go
      </span>
      {lights.map(l => (
        <span key={l.key} className={classNames("flex items-center gap-1.5 rounded-full border px-3 py-1", l.tint, l.ink)}>
          <l.Icon className="h-4 w-4" aria-hidden />
          <span className="font-bold text-gray-900">{data[l.key].toLocaleString()}</span>
          {l.label.toLowerCase()}
        </span>
      ))}
    </span>
  );
}

function ToGo({ data }: { data: IOrdersOverview }) {
  const counts = LIGHTS.map(l => data[l.key]);
  const widths = shares(counts);
  const lights = LIGHTS.filter(l => l.key !== "noDate" || data.noDate > 0);

  return (
    <section className="xl:col-span-2 flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-500">Orders to go</p>
          <p className="text-5xl font-bold tracking-tight text-gray-900 leading-none mt-1">{data.openOrders.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">{data.openParts.toLocaleString()} parts</p>
        </div>
        <Stages data={data} />
      </div>

      {/* Traffic light bar */}
      <div className="flex h-4 w-full gap-0.5 overflow-hidden rounded-full bg-gray-100" role="img"
        aria-label={LIGHTS.map((l, i) => `${l.label} ${counts[i]}`).join(", ")}>
        {LIGHTS.map((l, i) => widths[i] > 0 && (
          <Tooltip key={l.key} content={`${l.label}: ${counts[i]}`} className="h-full" >
            <div className={classNames("h-full first:rounded-l-full last:rounded-r-full", l.bar)} style={{ width: `${widths[i]}%`, minWidth: 6 }} />
          </Tooltip>
        ))}
      </div>

      <div className={classNames("grid gap-3", lights.length === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3")}>
        {lights.map(l => (
          <div key={l.key} className={classNames("rounded-lg border p-3", l.tint)}>
            <div className={classNames("flex items-center gap-1.5 text-sm font-semibold", l.ink)}>
              <l.Icon className="h-5 w-5" aria-hidden />
              {l.label}
            </div>
            <p className="text-3xl font-bold text-gray-900 mt-1">{data[l.key].toLocaleString()}</p>
            <p className="text-xs text-gray-500">{l.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Stages({ data }: { data: IOrdersOverview }) {
  const steps = [
    { label: "To pick", value: data.toPick },
    { label: "To pack", value: data.toPack },
    ...(data.labelled > 0 ? [{ label: "Labelled", value: data.labelled }] : []),
  ];

  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2">
          {i > 0 && <ArrowRightIcon className="h-4 w-4 text-gray-300" aria-hidden />}
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-center min-w-[84px]">
            <p className="text-xl font-bold text-gray-900 leading-none">{s.value.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Dispatched({ data }: { data: IOrdersOverview }) {
  const now = DateTime.now();
  const trend = dispatchedTrend(data.dispatchedToday, data.dispatchedSameTimeLastWeek);
  const max = Math.max(1, ...data.dispatchedByDay.map(d => d.orders));

  return (
    <section className="rounded-xl bg-primary-50 border border-primary-100 p-4 flex flex-col">
      <div className="flex items-center gap-1.5 text-sm font-medium text-primary-700">
        <TruckIcon className="h-5 w-5" aria-hidden />
        Dispatched today
      </div>
      <p className="text-5xl font-bold tracking-tight text-gray-900 leading-none mt-2">{data.dispatchedToday.toLocaleString()}</p>
      <p className={classNames("text-xs mt-1 font-medium", trendInk[trend.trend])}>
        {trend.text} <span className="text-gray-400 font-normal">(same time, {data.dispatchedSameTimeLastWeek})</span>
      </p>

      {/* Last 7 days: today live, earlier days from the half-hourly snapshot */}
      <div className="mt-auto pt-4">
        <p className="text-[11px] text-gray-400 mb-1">Last 7 days · earlier days {snapshotLabel(data.snapshotAt)}</p>
        <div className="flex items-end gap-1.5 h-20" role="img"
          aria-label={data.dispatchedByDay.map(d => `${dayLabel(d.date, now)} ${d.orders}`).join(", ")}>
          {data.dispatchedByDay.map(d => {
            const isToday = dayLabel(d.date, now) === "Today";
            return (
              <Tooltip key={d.date} content={`${DateTime.fromISO(d.date).toFormat("ccc d LLL")}: ${d.orders} orders`} className="flex-1 h-full flex items-end">
                <div className="w-full h-full flex items-end">
                  <div
                    className={classNames("w-full rounded-t", isToday ? "bg-primary-600" : "bg-primary-300")}
                    style={{ height: `${(d.orders / max) * 100}%`, minHeight: d.orders ? 4 : 2 }}
                  />
                </div>
              </Tooltip>
            );
          })}
        </div>
        <div className="flex gap-1.5 mt-1">
          {data.dispatchedByDay.map(d => (
            <span key={d.date} className={classNames("flex-1 text-center text-[11px]", dayLabel(d.date, now) === "Today" ? "font-semibold text-primary-700" : "text-gray-400")}>
              {dayLabel(d.date, now)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Speed({ data }: { data: IOrdersOverview }) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
        <h3 className="text-sm font-semibold text-gray-900">How fast are orders going out?</h3>
        <p className="text-xs text-gray-400">Typical time over the last 7 days, weekends left out · {snapshotLabel(data.snapshotAt)}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-3">
        <SpeedTile title="Time to pick" from="Sold" to="Picked" t={data.pickTime} />
        <ArrowRightIcon className="hidden md:block h-5 w-5 self-center text-gray-300" aria-hidden />
        <SpeedTile title="Time to pack" from="Picked" to="Labelled" t={data.packTime} />
        <ArrowRightIcon className="hidden md:block h-5 w-5 self-center text-gray-300" aria-hidden />
        <SpeedTile title="Sale to dispatch" from="Sold" to="Dispatched" t={data.saleToDispatch} highlight />
      </div>
    </section>
  );
}

function SpeedTile({ title, from, to, t, highlight = false }: { title: string; from: string; to: string; t: ILeadTime; highlight?: boolean }) {
  const trend = timeTrend(t);

  return (
    <div className={classNames("rounded-lg border p-4", highlight ? "border-primary-200 bg-primary-50" : "border-gray-200 bg-white")}>
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="text-xs text-gray-400">{from} → {to}</p>
      <p className="text-3xl font-bold text-gray-900 mt-2">{formatDuration(t.seconds)}</p>
      <p className={classNames("text-xs font-medium mt-1", trendInk[trend.trend])}>
        {trend.trend === "better" && "▼ "}
        {trend.trend === "worse" && "▲ "}
        {trend.text}
      </p>
      <p className="text-xs text-gray-400 mt-1">{t.count ? `From ${t.count.toLocaleString()} parts` : "Nothing timed this week"}</p>
    </div>
  );
}
